import { AlertOctagon, AlertTriangle, CheckCircle2, ShieldCheck, ShieldX } from 'lucide-react'
import { EmptyHint, Metric } from '../ui/Panels'
import { StateBadge } from '../ui/State'
import { fmtClock, fmtDegradedReason } from '../../lib/format'
import type { TrustAlert, TrustSnapshot } from '../../types'
import type { AsyncState } from '../../types'

/* ============================================================================
 * ПАНЕЛЬ ДОВЕРИЯ (ЗАЩИТА ОТ ПОДДЕЛОК И ПОВТОРНЫХ ВОСПРОИЗВЕДЕНИЙ)
 *
 * Данные с /api/trust:
 *   • alerts         — текущие тревоги;
 *   • anti_replay    — счётчики принятых и отклонённых сообщений;
 *   • auth_fail      — неуспешные попытки авторизации;
 *   • cross_domain   — контроль пересечения с судоходством;
 *   • commands_accepted — принятые команды.
 * ========================================================================= */

/** Текст состояния AsyncState, пригодный для бейджа */
function trustState(s: AsyncState<TrustSnapshot>['status']): 'approved' | 'pending' | 'warning' | 'critical-error' {
  switch (s) {
    case 'ok':
      return 'approved'
    case 'loading':
    case 'idle':
      return 'pending'
    case 'degraded':
      return 'warning'
    case 'error':
      return 'critical-error'
    default:
      return 'pending'
  }
}

/** Классификация критичности тревоги */
function alertState(a: TrustAlert): 'critical-error' | 'warning' | 'active' {
  const sev = String(a.severity ?? '').toLowerCase()
  const kind = String(a.kind ?? '').toLowerCase()
  if (sev.includes('crit') || kind.includes('intrud') || kind.includes('replay')) {
    return 'critical-error'
  }
  if (sev.includes('warn') || kind.includes('conflict') || kind.includes('clearance')) {
    return 'warning'
  }
  return 'active'
}

export function TrustPanel({
  trust,
  onRefresh,
}: {
  trust: AsyncState<TrustSnapshot>
  onRefresh: () => void
}) {
  const snap = trust.data
  const state = trustState(trust.status)
  const alerts = snap?.alerts ?? []

  return (
    <div className="space-y-2 p-2.5">
      {/* Состояние источника данных */}
      <div className={`rounded-md border px-2.5 py-2 ut-stated ut-state-${state}`}>
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-1.5 text-[11px] font-semibold">
            <ShieldCheck size={12} aria-hidden />
            Подсистема доверия
          </div>
          <StateBadge state={state}>
            {trust.status === 'ok'
              ? 'Норма'
              : trust.status === 'loading'
                ? 'Опрос'
                : trust.status === 'degraded'
                  ? 'Деградация'
                  : trust.status === 'error'
                    ? 'Нет связи'
                    : 'Ожидание'}
          </StateBadge>
        </div>
        {trust.status !== 'ok' && trust.error && (
          <div className="mt-1 text-[10px] text-ink-muted">
            {fmtDegradedReason(trust.degradedReason)} · {trust.error}
          </div>
        )}
      </div>

      {/* Счётчики anti-replay */}
      <div className="grid grid-cols-2 gap-2">
        <Metric
          label="Принято сообщений"
          value={snap?.anti_replay?.accepted ?? 0}
          state="approved"
        />
        <Metric
          label="Отклонено повторов"
          value={snap?.anti_replay?.replay_rejected ?? 0}
          state={(snap?.anti_replay?.replay_rejected ?? 0) > 0 ? 'warning' : 'active'}
        />
        <Metric
          label="Отклонено устаревших"
          value={snap?.anti_replay?.stale_rejected ?? 0}
          state={(snap?.anti_replay?.stale_rejected ?? 0) > 0 ? 'warning' : 'active'}
        />
        <Metric
          label="Ошибок авторизации"
          value={snap?.auth_fail ?? 0}
          state={(snap?.auth_fail ?? 0) > 0 ? 'critical-error' : 'approved'}
        />
        <Metric
          label="Активов заявлено"
          value={snap?.intent_assets ?? 0}
          state="active"
          title="Количество заявленных намерений (intent assets)"
        />
        <Metric
          label="Узлов доверия"
          value={snap?.anti_replay?.nodes ?? 0}
          state="active"
        />
      </div>

      {/* Междисциплинарный контроль */}
      <div className="rounded-md border border-line px-2 py-1.5">
        <div className="text-[10px] font-semibold uppercase tracking-wider text-ink-muted">
          Междисциплинарный контроль (судоходство)
        </div>
        <div className="ut-metric mt-1 flex flex-wrap gap-x-3 text-[10px] text-ink-muted">
          <span>Радиус: {snap?.cross_domain?.radius_m?.toFixed(0) ?? '—'} м</span>
          <span>Мин. запас: {snap?.cross_domain?.min_alt_clearance_m?.toFixed(0) ?? '—'} м</span>
          <span>Судов: {snap?.cross_domain?.vessels?.length ?? 0}</span>
          <span>Конфликтов: {snap?.cross_domain?.conflict_log_size ?? 0}</span>
        </div>
      </div>

      {/* Текущие тревоги */}
      <div>
        <div className="mb-1 flex items-center justify-between">
          <span className="text-[10px] font-semibold uppercase tracking-wider text-ink-muted">
            Тревоги ({alerts.length})
          </span>
          <button className="text-[10px] text-accent hover:underline" onClick={onRefresh}>
            Обновить
          </button>
        </div>

        {alerts.length === 0 ? (
          <div className="ut-stated ut-state-approved flex items-center gap-1.5 rounded-md border px-2 py-2 text-[11px]">
            <CheckCircle2 size={12} aria-hidden />
            Активных тревог нет.
          </div>
        ) : (
          <div className="space-y-1">
            {alerts.map((a, i) => {
              const st = alertState(a)
              return (
                <div
                  key={a.id ?? i}
                  className={`ut-stated ut-state-${st} rounded-md border px-2 py-1.5 text-[11px]`}
                >
                  <div className="flex items-start gap-1.5">
                    {st === 'critical-error' ? (
                      <AlertOctagon size={12} className="mt-0.5 shrink-0" aria-hidden />
                    ) : st === 'warning' ? (
                      <AlertTriangle size={12} className="mt-0.5 shrink-0" aria-hidden />
                    ) : (
                      <ShieldX size={12} className="mt-0.5 shrink-0" aria-hidden />
                    )}
                    <div className="min-w-0">
                      <div className="font-semibold">
                        {a.kind ?? 'ТРЕВОГА'}
                        {a.severity ? ` · ${a.severity}` : ''}
                      </div>
                      <div className="break-words text-ink-muted">{a.message ?? '—'}</div>
                      <div className="ut-metric text-[10px] text-ink-muted">{fmtClock(a.ts)}</div>
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>

      {alerts.length === 0 && trust.status === 'idle' && (
        <EmptyHint>Ожидание первой загрузки данных доверия…</EmptyHint>
      )}
    </div>
  )
}
